import {
  API_ROUTES,
  addChatDocument,
  getChatDocument,
  postEncryptedJson,
} from "../utils/apiClient";
import AppConstants from "../utils/appConstants";

function pickSenderRoot(m) {
  return m?.sender ?? m?.SENDER ?? m?.sender_device ?? m?.senderDevice ?? null;
}

/**
 * Same idea as Flutter ChatModel: sender.user_login.ID
 */
function extractSenderUserIdFromRaw(m) {
  const s = pickSenderRoot(m);
  if (!s || typeof s !== "object") return null;
  const ul = s.user_login ?? s.USER ?? s.user ?? s.userLogin;
  if (ul && typeof ul === "object") {
    const id = ul.ID ?? ul.id ?? ul.USER_ID ?? ul.user_id;
    if (id != null && id !== "") return String(id);
  }
  return null;
}

function extractSenderNickFromRaw(m) {
  const s = pickSenderRoot(m);
  if (!s || typeof s !== "object") return "";
  const ul = s.user_login ?? s.USER ?? s.user ?? s.userLogin;
  if (ul && typeof ul === "object") {
    return String(ul.NICK_NAME ?? ul.nick_name ?? ul.USERNAME ?? ul.username ?? ul.EMAIL ?? ul.email ?? "").trim();
  }
  return "";
}

export function mapRawChatRowToMessage(m) {
  const nested =
    m.chat_message || m.chatMessage || m.CHAT_MESSAGE || m.message || null;

  const text =
    m.MESSAGE ??
    (typeof m.message === "string" ? m.message : null) ??
    (nested && typeof nested.MESSAGE === "string" ? nested.MESSAGE : null) ??
    (nested && typeof nested.message === "string" ? nested.message : null) ??
    "";

  const fromUser =
    m.FROM_USER != null
      ? m.FROM_USER
      : m.IS_FROM_USER != null
        ? m.IS_FROM_USER
        : m.IS_ME_SENDER != null
          ? m.IS_ME_SENDER
          : null;

  const senderType =
    m.SENDER_TYPE ??
    m.SENDER ??
    m.MESSAGE_FROM ??
    nested?.SENDER_TYPE ??
    nested?.SENDER ??
    null;

  const messageType =
    m.MESSAGE_TYPE ??
    m.message_type ??
    nested?.MESSAGE_TYPE ??
    nested?.message_type ??
    null;

  const userId =
    m.USER_ID ??
    m.user_id ??
    m.USER?.ID ??
    m.user?.id ??
    nested?.USER_ID ??
    nested?.user_id ??
    null;

  const createdAt =
    m.CREATED_AT ||
    m.CREATED_AT_DATE ||
    m.created_at ||
    m.DATE ||
    m.date ||
    null;

  const senderUserId = extractSenderUserIdFromRaw(m);
  const senderNickName = extractSenderNickFromRaw(m);

  return {
    id: m.ID ?? m.id ?? m.MESSAGE_ID ?? m.messageId ?? m.CHAT_ID ?? m.chatID ?? null,
    text,
    fromUser,
    senderType,
    messageType,
    userId,
    senderUserId,
    senderNickName,
    createdAt,
    raw: m,
  };
}

/**
 * Chat Controller
 * Encapsulates support chat APIs (get messages, send message).
 * Uses encrypted transport + Bearer token via postEncryptedJson (matches Flutter behavior).
 */
class ChatController {
  /**
   * Fetch chat messages.
   *
   * @param {Object} params
   * @param {number} [params.page=1] - 1-based page index (sent as ?page=).
   * @param {number} [params.perPage=20] - Messages per page.
   * @param {Object} [options] - Optional postEncryptedJson options (e.g., baseURL override).
   *
   * @returns {Promise<{
   *   messages: Array,
   *   pagination: { page: number, perPage: number, total?: number, lastPage?: number },
   *   raw: any
   * }>}
   */
  static async getMessages({ page = 1, perPage = 20 } = {}, options = {}) {
    const endpoint = `${API_ROUTES.GET_CHAT_MESSAGE}?page=${page}`;

    const body = {
      PER_PAGE: perPage,
    };

    const res = await postEncryptedJson(endpoint, body, options);
    const d = res.decryptedData || res.data || {};

    if (!d || Number(d.CODE) !== AppConstants.responseOk) {
      const msg = d.MESSAGE || d.message || `API Error (CODE: ${d.CODE ?? "Unknown"})`;
      throw new Error(msg);
    }

    const dataBlock = d.DATA || {};
    let list = [];

    if (Array.isArray(dataBlock.data)) {
      list = dataBlock.data;
    } else if (Array.isArray(d.DATA)) {
      list = d.DATA;
    } else if (Array.isArray(dataBlock.items)) {
      list = dataBlock.items;
    } else if (Array.isArray(dataBlock.messages)) {
      list = dataBlock.messages;
    }

    const messages = list.map((m) => mapRawChatRowToMessage(m));

    const currentPage = Number(dataBlock.current_page ?? page ?? 1);
    const lastPage = Number(dataBlock.last_page ?? currentPage);
    const perPageVal = Number(dataBlock.per_page ?? perPage) || perPage;
    const totalVal = Number(dataBlock.total ?? dataBlock.TOTAL ?? messages.length);

    const pagination = {
      page: currentPage,
      perPage: perPageVal,
      total: totalVal,
      lastPage,
    };

    return { messages, pagination, raw: d };
  }

  /**
   * Single message by CHAT_ID — same as Flutter APIURI.GET_CHAT_MESSAGE (get-a-message).
   */
  static async getMessageByChatId(chatId, options = {}) {
    const id = chatId != null ? String(chatId).trim() : "";
    if (!id) {
      throw new Error("CHAT_ID is required.");
    }

    const res = await postEncryptedJson(API_ROUTES.GET_A_CHAT_MESSAGE, { CHAT_ID: id }, options);
    const d = res.decryptedData || res.data || {};

    if (!d || Number(d.CODE) !== AppConstants.responseOk) {
      const msg = d.MESSAGE || d.message || `API Error (CODE: ${d.CODE ?? "Unknown"})`;
      throw new Error(msg);
    }

    const row = d.DATA;
    if (!row || typeof row !== "object") {
      throw new Error("Invalid message payload.");
    }

    return { message: mapRawChatRowToMessage(row), raw: d };
  }

  /**
   * Send a new chat message.
   *
   * @param {Object} params
   * @param {string} params.message - Message text.
   * @param {number|string|null} [params.userId=null] - Optional USER_ID to send with the message.
   * @param {Object} [options] - Optional postEncryptedJson options.
   *
   * @returns {Promise<{ raw: any }>}
   */
  static async sendMessage({ message, userId = null }, options = {}) {
    if (!message || typeof message !== "string" || !message.trim()) {
      throw new Error("Message text is required.");
    }

    const body = {
      MESSAGE: message.trim(),
      USER_ID:
        userId != null
          ? (typeof userId === "string" ? userId : String(userId))
          : null,
    };

    const res = await postEncryptedJson(API_ROUTES.ADD_CHAT_MESSAGE, body, options);
    const d = res.decryptedData || res.data || {};

    if (!d || Number(d.CODE) !== AppConstants.responseOk) {
      const msg = d.MESSAGE || d.message || `API Error (CODE: ${d.CODE ?? "Unknown"})`;
      throw new Error(msg);
    }

    return { raw: d };
  }

  /**
   * Upload a chat document.
   * @param {Object} params
   * @param {File|Blob} params.file
   * @param {number} [params.chatType=0]
   * @param {number} [params.messageType=1]
   */
  static async uploadChatDocument(
    { file, chatType = 0, messageType = 3 } = {},
    options = {}
  ) {
    const response = await addChatDocument(file, { chatType, messageType }, options);
    const d = response?.data || {};
    if (!d || Number(d.CODE) !== AppConstants.responseOk) {
      const msg = d.MESSAGE || d.message || `API Error (CODE: ${d.CODE ?? "Unknown"})`;
      throw new Error(msg);
    }
    return { raw: d };
  }

  /**
   * Download a chat document blob and provide an object URL for preview/download.
   */
  static async getChatDocumentUrl(documentName, options = {}) {
    const response = await getChatDocument(documentName, options);
    const blob = response?.data;
    if (!(blob instanceof Blob) || blob.size === 0) {
      throw new Error("Invalid document payload.");
    }
    return {
      blob,
      url: URL.createObjectURL(blob),
      contentType: blob.type || "application/octet-stream",
    };
  }
}

export default ChatController;
